import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarClock, Film, Mic, Sparkles, Youtube, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import heroStack from "@/assets/hero-stack.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "AutoTubber — Post AI Videos on Auto-Pilot" },
      {
        name: "description",
        content:
          "Create a faceless short-form video series once. AutoTubber writes, narrates, edits and posts every episode to YouTube, Instagram and Facebook.",
      },
      { property: "og:title", content: "AutoTubber — Post AI Videos on Auto-Pilot" },
      {
        property: "og:description",
        content: "Faceless YouTube Shorts and Reels, generated and published automatically on your schedule.",
      },
    ],
  }),
  component: Index,
});

const steps = [
  { icon: Sparkles, title: "Pick a topic", text: "Choose a category or write your own niche." },
  { icon: Mic, title: "Pick a narrator & voice", text: "Faceless personas with preset AI voices." },
  { icon: Film, title: "Episodes assemble", text: "Script, voiceover, visuals and burned-in captions." },
  { icon: CalendarClock, title: "Auto-post forever", text: "Published on your schedule, every platform." },
];

function Index() {
  const { ready, user } = useAuth();
  const signedIn = Boolean(ready && user);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-primary/20 blur-[130px]" />
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:py-28">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Faceless video automation
              </span>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
                Post AI Videos on{" "}
                <span className="bg-gradient-primary bg-clip-text text-transparent">Auto-Pilot</span>
              </h1>
              <p className="mt-5 max-w-lg text-lg text-muted-foreground">
                Set up your series once. AutoTubber generates the script, voiceover, visuals and captions —
                then publishes new episodes on your schedule.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button size="lg" asChild>
                  <Link to={env.routes.onboarding}>
                    {signedIn ? env.labels.newSeries : env.labels.getStarted}{" "}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="secondary" asChild>
                  <Link to={signedIn ? env.routes.dashboard : env.routes.login}>
                    {signedIn ? env.labels.dashboard : env.labels.login}
                  </Link>
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-5 text-muted-foreground">
                <span className="text-xs uppercase tracking-widest">Publishes to</span>
                <Youtube className="h-6 w-6 text-primary" />
                <Instagram className="h-5 w-5" />
                <Facebook className="h-5 w-5" />
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-border shadow-card">
                <img
                  src={heroStack}
                  alt="Stack of AI-generated faceless short-form video previews"
                  width={1280}
                  height={1024}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 left-6 rounded-xl border border-border bg-card/95 px-4 py-3 text-sm shadow-glow backdrop-blur">
                <p className="font-medium">Episode 43 published</p>
                <p className="text-xs text-muted-foreground">YouTube Shorts · 2 min ago</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border/60 bg-gradient-surface">
          <div className="mx-auto max-w-6xl px-5 py-20">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">How it works</h2>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s, i) => (
                <div
                  key={s.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card transition-colors hover:border-primary/50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground">Step {i + 1}</p>
                  <h3 className="mt-1 font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="rounded-3xl border border-border bg-card p-10 text-center shadow-card">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Your next 30 videos are already written
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              {signedIn
                ? "Open your dashboard or create another series — no editing software, no camera, no face."
                : "Start free — configure a series in under two minutes. No editing software, no camera, no face."}
            </p>
            <Button size="lg" className="mt-7" asChild>
              <Link to={signedIn ? env.routes.dashboard : env.routes.onboarding}>
                {signedIn ? env.labels.dashboard : "Create my series"}{" "}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 py-8 text-center text-sm text-muted-foreground">
        © 2026 AutoTubber. Faceless video series on auto-pilot.
      </footer>
    </div>
  );
}
